import type { MeResponse, NudgeState, UserPreferences } from "@starsach/shared-types";
import type { Session } from "../auth/auth";

/**
 * Nudges: one banner at a time asking the user to finish something. The
 * registry is the list of things the product would like done; each entry
 * says when it applies. What the user did about it lives in preferences
 * (`nudges[id]`), so it follows them between devices: "later" hides the
 * nudge until the next sign-in (compared with the token's auth_time), and
 * "never" hides it for good. The banner never shows on the login and
 * callback pages, nor inside a view-as tab (the settings are the staff
 * member's own, and the customer's portal is not the place).
 */
export interface NudgeFacts {
  /** How many passkeys the user has; undefined while unknown, so the passkey nudge waits rather than flashing. */
  passkeyCount?: number | undefined;
  /** WebAuthn is available in this browser. */
  passkeysPossible: boolean;
}

export interface Nudge {
  id: string;
  title: string;
  body: string;
  /** Where "Enable" goes. */
  action: { label: string; to: string };
  applies: (me: MeResponse, facts: NudgeFacts) => boolean;
  /** Paths the nudge is not shown on - typically the page it points at. */
  quietOn: readonly string[];
}

export const NUDGES: readonly Nudge[] = [
  {
    id: "passkey",
    title: "Sign in with a touch",
    body: "A passkey signs you in with your fingerprint, face or security key - no password, no code - and confirms it's you with a touch. Enrol one on this device.",
    action: { label: "Enrol a passkey", to: "/account#security" },
    // A federated user's passkeys are their identity provider's business (ADR-0014).
    applies: (me, facts) => !me.identityProvider && facts.passkeysPossible && facts.passkeyCount === 0,
    quietOn: ["/account"],
  },
];

/** When this sign-in happened: the sign-in the user made, falling back to the token's auth_time for a session that predates the marker. */
function signedIn(session: Pick<Session, "authTime" | "signedInAt"> | undefined): number {
  return session?.signedInAt ?? (session?.authTime ?? 0) * 1000;
}

/** Whether the user's recorded answer still hides the nudge. */
export function snoozed(state: NudgeState | undefined, session: Pick<Session, "authTime" | "signedInAt"> | undefined): boolean {
  if (!state) return false;
  if (state.state === "never") return true;
  // "later" records the sign-in it was said in; any newer sign-in clears it. A step-up is not a sign-in: it renews
  // auth_time but not signedInAt, so it does not bring the nudge back.
  const answeredIn = Date.parse(state.at);
  return !Number.isFinite(answeredIn) || signedIn(session) <= answeredIn;
}

/** The value to store for "later": this sign-in, so the next one is what brings the nudge back. */
export function laterAt(session: Pick<Session, "authTime" | "signedInAt"> | undefined): string {
  return new Date(signedIn(session) || Date.now()).toISOString();
}

export function visibleNudge(
  me: MeResponse | undefined,
  preferences: Pick<UserPreferences, "nudges">,
  session: Pick<Session, "authTime" | "signedInAt"> | undefined,
  facts: NudgeFacts,
  pathname: string,
): Nudge | undefined {
  if (!me || me.viewAs) return undefined;
  return NUDGES.find((n) => !n.quietOn.some((p) => pathname === p || pathname.startsWith(`${p}/`)) && !snoozed(preferences.nudges[n.id], session) && n.applies(me, facts));
}
